import React, { useState, useRef, useEffect } from 'react';
import { VideoTile } from './VideoTile';
import { MonitorUp, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

interface Screen {
  id: string;
  participantName: string;
  isScreenShare: boolean;
  track?: MediaStreamTrack;
}

interface ScreenGridProps {
  screens: Screen[];
  maxScreens?: number;
}

export const ScreenGrid: React.FC<ScreenGridProps> = ({
  screens,
  maxScreens = 4,
}) => {
  const [enlargedScreen, setEnlargedScreen] = useState<Screen | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const displayScreens = screens.slice(0, maxScreens);
  const screenCount = displayScreens.length;

  // Scroll to specific index (mobile)
  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const screenWidth = container.offsetWidth;
    container.scrollTo({ left: screenWidth * index, behavior: 'smooth' });
    setCurrentIndex(index);
  };

  // Detect scroll position (mobile)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const index = Math.round(container.scrollLeft / container.offsetWidth);
      setCurrentIndex(index);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard navigation in enlarged view
  useEffect(() => {
    if (!enlargedScreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEnlargedScreen(null);
      } else if (e.key === 'ArrowLeft') {
        const idx = displayScreens.findIndex(s => s.id === enlargedScreen.id);
        if (idx > 0) setEnlargedScreen(displayScreens[idx - 1]);
      } else if (e.key === 'ArrowRight') {
        const idx = displayScreens.findIndex(s => s.id === enlargedScreen.id);
        if (idx < displayScreens.length - 1) setEnlargedScreen(displayScreens[idx + 1]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enlargedScreen, displayScreens]);

  if (displayScreens.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-neutral-900/50 border border-white/10 rounded-xl">
        <div className="text-center px-4">
          <MonitorUp size={48} className="mx-auto text-neutral-600 mb-3" />
          <p className="text-neutral-400 font-medium mb-1">No screens shared yet</p>
          <p className="text-sm text-neutral-500">Click "Share Screen" to get started</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop: 2x2 Grid (Always visible on md+) */}
      <div className="hidden md:grid w-full h-full gap-3 auto-rows-fr"
        style={{
          gridTemplateColumns: screenCount === 1 ? '1fr' : 'repeat(2, 1fr)',
          gridTemplateRows: screenCount <= 2 ? '1fr' : 'repeat(2, 1fr)'
        }}
      >
        {displayScreens.map((screen, index) => (
          <div 
            key={screen.id} 
            className={`relative min-h-0 group ${
              screenCount === 3 && index === 2 ? 'col-span-2' : ''
            }`}
          >
            <VideoTile
              participantName={screen.participantName}
              isScreenShare={screen.isScreenShare}
              videoTrack={screen.track}
              className="w-full h-full"
            />
            {/* Enlarge Button - Desktop */}
            <button
              onClick={() => setEnlargedScreen(screen)}
              className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white p-2.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 z-10"
              title="Enlarge (Click)"
            >
              <Maximize2 size={18} />
            </button>
          </div>
        ))}
      </div>

      {/* Mobile: Horizontal Swipe Carousel */}
      <div className="md:hidden h-full relative">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-x-auto snap-x snap-mandatory flex scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {displayScreens.map((screen) => (
            <div key={screen.id} className="min-w-full h-full snap-center flex-shrink-0 px-2">
              <div className="relative h-full">
                <VideoTile
                  participantName={screen.participantName}
                  isScreenShare={screen.isScreenShare}
                  videoTrack={screen.track}
                  className="h-full"
                />
                {/* Enlarge Button - Mobile */}
                <button
                  onClick={() => setEnlargedScreen(screen)}
                  className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white p-2.5 rounded-lg active:scale-95 transition-transform z-10"
                  title="Enlarge"
                >
                  <Maximize2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Mobile */}
        {screenCount > 1 && (
          <>
            <button
              onClick={() => scrollToIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className={`absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 backdrop-blur-sm text-white p-2 rounded-full transition-all ${
                currentIndex === 0 ? 'opacity-30' : 'hover:bg-black/80 active:scale-95'
              }`}
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scrollToIndex(Math.min(screenCount - 1, currentIndex + 1))}
              disabled={currentIndex === screenCount - 1}
              className={`absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 backdrop-blur-sm text-white p-2 rounded-full transition-all ${
                currentIndex === screenCount - 1 ? 'opacity-30' : 'hover:bg-black/80 active:scale-95'
              }`}
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Position Indicator - Mobile */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
          {currentIndex + 1} / {screenCount}
        </div>
      </div>

      {/* Enlarged View Modal */}
      {enlargedScreen && (
        <div className="fixed inset-0 z-50 bg-black/98 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={() => setEnlargedScreen(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-xl z-20 transition-all"
            title="Close (Esc)"
          >
            <X size={24} />
          </button>

          {/* Navigation Arrows */}
          {screenCount > 1 && (
            <>
              <button
                onClick={() => {
                  const idx = displayScreens.findIndex(s => s.id === enlargedScreen.id);
                  if (idx > 0) setEnlargedScreen(displayScreens[idx - 1]);
                }}
                disabled={displayScreens.findIndex(s => s.id === enlargedScreen.id) === 0}
                className={`absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm text-white p-3 rounded-xl z-20 transition-all ${
                  displayScreens.findIndex(s => s.id === enlargedScreen.id) === 0
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-white/20'
                }`}
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={() => {
                  const idx = displayScreens.findIndex(s => s.id === enlargedScreen.id);
                  if (idx < displayScreens.length - 1) setEnlargedScreen(displayScreens[idx + 1]);
                }}
                disabled={displayScreens.findIndex(s => s.id === enlargedScreen.id) === screenCount - 1}
                className={`absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm text-white p-3 rounded-xl z-20 transition-all ${
                  displayScreens.findIndex(s => s.id === enlargedScreen.id) === screenCount - 1
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-white/20'
                }`}
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}

          {/* Enlarged Video */}
          <div className="w-full h-full max-w-7xl max-h-full p-4">
            <VideoTile
              participantName={enlargedScreen.participantName}
              isScreenShare={enlargedScreen.isScreenShare}
              videoTrack={enlargedScreen.track}
              className="w-full h-full"
            />
          </div>

          {/* Screen Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full font-medium">
            {displayScreens.findIndex(s => s.id === enlargedScreen.id) + 1} / {screenCount}
          </div>

          {/* Keyboard Hints - Desktop Only */}
          <div className="hidden md:block absolute top-4 left-4 bg-white/10 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-xl">
            <div className="flex gap-4">
              <span>← → Navigate</span>
              <span>Esc Close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};